import { useState, useRef, useEffect, useCallback } from 'react'
import { Search, Play, Pause, Plus, Music, X } from 'lucide-react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { cn } from '@/lib/utils'

const CATEGORY_LABELS: Record<string, string> = {
  chill: 'Chill',
  classical: 'Classical',
  comedy: 'Comedy',
  country: 'Country',
  electronic: 'Electronic',
  'epic-dramatic': 'Epic Dramatic',
  fantasy: 'Fantasy',
  horror: 'Horror',
  jazz: 'Jazz',
  rock: 'Rock',
  'romantic-sentimental': 'Romantic',
  underscoring: 'Underscoring',
  uplifting: 'Uplifting',
  world: 'World',
}

interface MusicPickerProps {
  workflowId: Id<"workflows">
  onClose: () => void
  totalDuration?: number
}

export function MusicPicker({ workflowId, onClose, totalDuration = 30 }: MusicPickerProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [playingTrackId, setPlayingTrackId] = useState<string | null>(null)
  const [addingTrackId, setAddingTrackId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 250)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const tracks = useQuery(api.musicLibrary.listTracks, {
    category: selectedCategory ?? undefined,
    query: debouncedQuery || undefined,
  })

  const addAudioTrack = useMutation(api.audioGeneration.addAudioTrack)

  useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  const handlePlay = useCallback((trackId: string, url: string) => {
    if (playingTrackId === trackId) {
      audioRef.current?.pause()
      setPlayingTrackId(null)
      return
    }

    if (audioRef.current) {
      audioRef.current.pause()
    }

    const audio = new Audio(url)
    audioRef.current = audio
    audio.volume = 0.5
    audio.addEventListener('ended', () => setPlayingTrackId(null), { once: true })
    audio.addEventListener('error', () => setPlayingTrackId(null), { once: true })
    audio.play().catch(() => setPlayingTrackId(null))
    setPlayingTrackId(trackId)
  }, [playingTrackId])

  const handleAddToTimeline = useCallback(async (track: { id: string; title: string; url: string }) => {
    setAddingTrackId(track.id)
    try {
      await addAudioTrack({
        workflowId,
        type: 'music',
        name: track.title,
        url: track.url,
        startTime: 0,
        duration: totalDuration,
        volume: 0.3,
        fadeIn: 1,
        fadeOut: 2,
      })
    } catch (e) {
      console.error('Failed to add track:', e)
    } finally {
      setAddingTrackId(null)
    }
  }, [workflowId, addAudioTrack, totalDuration])

  const displayTracks = tracks ?? []

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <div className="flex items-center gap-2">
          <Music size={15} className="text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground tracking-tight">Music</span>
          {tracks !== undefined && (
            <span className="text-[10px] tabular-nums text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
              {displayTracks.length}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-muted rounded-md text-muted-foreground hover:text-secondary-foreground transition-colors"
        >
          <X size={15} />
        </button>
      </div>

      <div className="px-3 py-2 space-y-2 border-b border-border">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search tracks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-secondary border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring placeholder:text-muted-foreground"
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); setDebouncedQuery('') }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded text-muted-foreground"
            >
              <X size={12} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(selectedCategory === key ? null : key)}
              className={cn(
                'shrink-0 px-2 py-1 text-xs rounded-md border transition-colors',
                selectedCategory === key
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-background text-muted-foreground border-border hover:border-border/80 hover:text-secondary-foreground'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {(selectedCategory || searchQuery) && (
          <button
            onClick={() => { setSelectedCategory(null); setSearchQuery(''); setDebouncedQuery('') }}
            className="text-[10px] text-muted-foreground hover:text-secondary-foreground transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {tracks === undefined ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <div className="w-5 h-5 border-2 border-border border-t-muted-foreground rounded-full animate-spin mb-2" />
            <p className="text-xs">Loading tracks...</p>
          </div>
        ) : displayTracks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Music size={28} className="mb-2 opacity-40" />
            <p className="text-xs">No tracks match your filters</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {displayTracks.map((track) => (
              <TrackRow
                key={track.id}
                track={track}
                isPlaying={playingTrackId === track.id}
                isAdding={addingTrackId === track.id}
                onPlay={() => handlePlay(track.id, track.url)}
                onAdd={() => handleAddToTimeline(track)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function TrackRow({
  track,
  isPlaying,
  isAdding,
  onPlay,
  onAdd,
}: {
  track: {
    id: string
    title: string
    category: string
    url: string
  }
  isPlaying: boolean
  isAdding: boolean
  onPlay: () => void
  onAdd: () => void
}) {
  return (
    <div className={cn(
      'group flex items-center gap-2.5 px-3 py-2 transition-colors hover:bg-accent',
      isPlaying && 'bg-primary/5'
    )}>
      <button
        onClick={onPlay}
        className={cn(
          'shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all',
          isPlaying
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-secondary-foreground'
        )}
      >
        {isPlaying ? <Pause size={13} /> : <Play size={13} className="ml-0.5" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={cn(
            'text-sm font-medium truncate',
            isPlaying ? 'text-primary' : 'text-foreground'
          )}>
            {track.title}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-[10px] text-muted-foreground">{CATEGORY_LABELS[track.category] ?? track.category}</span>
          {isPlaying && (
            <>
              <span className="text-[10px] text-border">|</span>
              <span className="flex items-center gap-[2px]">
                {[8, 12, 6, 10].map((h, i) => (
                  <span
                    key={i}
                    className="inline-block w-[2px] bg-primary rounded-full animate-pulse"
                    style={{
                      height: `${h}px`,
                      animationDelay: `${i * 0.15}s`,
                      animationDuration: '0.6s',
                    }}
                  />
                ))}
              </span>
            </>
          )}
        </div>
      </div>

      <button
        onClick={onAdd}
        disabled={isAdding}
        className={cn(
          'shrink-0 p-1.5 rounded-md transition-colors',
          isAdding
            ? 'text-border'
            : 'text-muted-foreground hover:text-primary hover:bg-primary/10'
        )}
        title="Add to timeline"
      >
        <Plus size={15} />
      </button>
    </div>
  )
}
